// Página de preferencias de notificaciones. Solo lógica: toggle global,
// quiet hours, tipos por rol, botón de prueba, instrucciones para "denied".

import { useEffect, useState } from "react";
import api from "../lib/api";
import Topbar from "../components/Topbar";
import { Bell, BellOff, BellRing, Clock, ShieldOff } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import {
  isPushSupported,
  permissionState,
  activatePush,
  deactivatePush,
  sendTestNotification,
} from "../lib/pushClient";

const TYPES_BY_ROLE = {
  legal: ["supplier_docs_for_legal_review", "supplier_doc_resubmitted"],
  corporate_admin: [
    "oc_created", "supplier_registered", "supplier_completed_registration",
    "supplier_legal_approved", "supplier_legal_rejected",
    "invoice_received", "invoice_price_mismatch", "invoice_receipt_mismatch",
    "receipt_discrepancy", "oc_cancelled",
  ],
  treasury: ["invoice_validated", "operating_expense_due"],
  branch_manager: ["oc_approved_for_receipt"],
  supplier: ["oc_received", "invoice_validated", "invoice_rejected", "payment_executed", "price_change_request"],
};

export default function NotificationsSettings() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [prefs, setPrefs] = useState(null);
  const [permission, setPermission] = useState(() => permissionState());
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const r = await api.get("/notifications/preferences");
      if (!cancelled) setPrefs(r.data);
    })();
    return () => { cancelled = true; };
  }, []);

  const reload = async () => {
    const r = await api.get("/notifications/preferences");
    setPrefs(r.data);
  };

  const supported = isPushSupported();
  const roleTypes = TYPES_BY_ROLE[user?.role] || [];

  const handleActivate = async () => {
    setBusy(true);
    try {
      const res = await activatePush();
      if (res.ok) {
        toast.success(t("push.activated"));
        setPermission("granted");
        await sendTestNotification();
      } else if (res.reason === "denied") {
        setPermission("denied");
        toast.error(t("push.denied"));
      } else {
        toast.error(t("push.activateFailed"));
      }
    } finally {
      setBusy(false);
    }
  };

  const handleDeactivate = async () => {
    setBusy(true);
    try {
      await deactivatePush();
      toast.success(t("push.deactivated"));
    } finally {
      setBusy(false);
    }
  };

  const handleTest = async () => {
    setBusy(true);
    try {
      const r = await sendTestNotification();
      if (r.sent > 0) toast.success(t("push.testSent"));
      else toast.message(t("push.testNoSubs"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("common.error"));
    } finally {
      setBusy(false);
    }
  };

  const updatePref = async (patch) => {
    setBusy(true);
    try {
      const r = await api.put("/notifications/preferences", patch);
      setPrefs(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("common.error"));
    } finally {
      setBusy(false);
    }
  };

  const toggleType = (typeKey) => {
    const current = prefs?.types_enabled || {};
    const next = { ...current, [typeKey]: !(current[typeKey] !== false) };
    updatePref({ types_enabled: next });
  };

  if (!prefs) {
    return (
      <>
        <Topbar />
        <div className="p-8 text-sm text-slate-500">{t("common.loading")}</div>
      </>
    );
  }

  return (
    <>
      <Topbar />
      <div className="p-6 sm:p-8 max-w-3xl mx-auto space-y-6" data-testid="notifications-settings-page">
        <header className="flex items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-2">
              <BellRing className="w-5 h-5 text-[#1A1A1A]" />
              <h1 className="font-display text-2xl font-bold text-[#1A1A1A]">{t("push.settings.title")}</h1>
            </div>
            <p className="text-sm text-slate-600 mt-1 leading-relaxed max-w-xl">{t("push.settings.subtitle")}</p>
          </div>
        </header>

        {!supported && (
          <div data-testid="push-unsupported" className="bg-slate-100 border border-slate-200 rounded-2xl p-4 text-sm text-slate-700 flex gap-3">
            <ShieldOff className="w-5 h-5 flex-shrink-0 text-slate-500" />
            <div>{t("push.settings.unsupported")}</div>
          </div>
        )}

        {supported && permission === "denied" && (
          <div data-testid="push-denied-block" className="bg-amber-50 border border-amber-200 rounded-2xl p-4 text-sm text-amber-900 flex gap-3">
            <BellOff className="w-5 h-5 flex-shrink-0" />
            <div>{t("push.settings.deniedInstructions")}</div>
          </div>
        )}

        {supported && permission !== "denied" && (
          <section className="bg-white rounded-2xl border border-slate-200 p-5">
            <div className="flex items-center justify-between gap-3">
              <div>
                <div className="text-sm font-bold text-[#1A1A1A]">{t("push.settings.osTitle")}</div>
                <div className="text-xs text-slate-600 mt-0.5">
                  {permission === "granted" ? t("push.settings.osActive") : t("push.settings.osInactive")}
                </div>
              </div>
              {permission === "granted" ? (
                <div className="flex items-center gap-2">
                  <button data-testid="push-send-test-btn" disabled={busy} onClick={handleTest}
                    className="px-3 py-2 bg-[#FFDF00] text-[#1A1A1A] rounded-xl text-xs font-bold hover:bg-[#FFD500] disabled:opacity-50">
                    {t("push.settings.sendTest")}
                  </button>
                  <button data-testid="push-deactivate-btn" disabled={busy} onClick={handleDeactivate}
                    className="px-3 py-2 bg-white border border-slate-300 text-slate-700 rounded-xl text-xs font-bold hover:bg-slate-50 disabled:opacity-50">
                    {t("push.settings.deactivate")}
                  </button>
                </div>
              ) : (
                <button data-testid="push-activate-btn" disabled={busy} onClick={handleActivate}
                  className="px-4 py-2 bg-[#1A1A1A] text-[#FFDF00] rounded-xl text-xs font-bold hover:bg-black disabled:opacity-50">
                  {busy ? t("push.banner.activating") : t("push.settings.activate")}
                </button>
              )}
            </div>
          </section>
        )}

        <section className="bg-white rounded-2xl border border-slate-200 p-5 space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-sm font-bold text-[#1A1A1A]">{t("push.settings.globalToggle")}</div>
              <div className="text-xs text-slate-600 mt-0.5">{t("push.settings.globalToggleHint")}</div>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                data-testid="push-pref-global-toggle"
                type="checkbox"
                checked={prefs.push_enabled !== false}
                onChange={(e) => updatePref({ push_enabled: e.target.checked })}
                disabled={busy}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-[#FFDF00] rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#1A1A1A]"></div>
            </label>
          </div>

          <div className="border-t border-slate-100 pt-4 space-y-3">
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4 text-slate-500" />
              <div className="text-sm font-bold text-[#1A1A1A]">{t("push.settings.quietHours")}</div>
            </div>
            <p className="text-xs text-slate-600 leading-relaxed">{t("push.settings.quietHoursHint")}</p>
            <div className="flex items-center gap-3">
              <label className="text-xs text-slate-700 font-semibold">{t("push.settings.from")}</label>
              <input
                data-testid="push-pref-quiet-start"
                type="time"
                value={prefs.quiet_hours_start || ""}
                onChange={(e) => updatePref({ quiet_hours_start: e.target.value })}
                className="px-2 py-1.5 bg-slate-50 border border-slate-200 rounded-lg text-sm"
              />
              <label className="text-xs text-slate-700 font-semibold">{t("push.settings.to")}</label>
              <input
                data-testid="push-pref-quiet-end"
                type="time"
                value={prefs.quiet_hours_end || ""}
                onChange={(e) => updatePref({ quiet_hours_end: e.target.value })}
                className="px-2 py-1.5 bg-slate-50 border border-slate-200 rounded-lg text-sm"
              />
              {(prefs.quiet_hours_start || prefs.quiet_hours_end) && (
                <button
                  data-testid="push-pref-quiet-clear"
                  onClick={() => updatePref({ quiet_hours_start: "", quiet_hours_end: "" })}
                  className="text-xs text-slate-500 underline decoration-dotted"
                >
                  {t("push.settings.clear")}
                </button>
              )}
            </div>
          </div>
        </section>

        {roleTypes.length > 0 && (
          <section className="bg-white rounded-2xl border border-slate-200 p-5">
            <div className="text-sm font-bold text-[#1A1A1A] mb-1">{t("push.settings.typesTitle")}</div>
            <p className="text-xs text-slate-600 leading-relaxed mb-4">{t("push.settings.typesHint")}</p>
            <div className="space-y-2">
              {roleTypes.map((tk) => {
                const enabled = (prefs.types_enabled || {})[tk] !== false;
                return (
                  <div key={tk} className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0">
                    <div>
                      <div className="text-sm text-[#1A1A1A] font-semibold">{t(`push.types.${tk}`, tk)}</div>
                      <div className="text-xs text-slate-500">{t(`push.typesDesc.${tk}`, "")}</div>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        data-testid={`push-pref-type-${tk}`}
                        type="checkbox"
                        checked={enabled}
                        onChange={() => toggleType(tk)}
                        disabled={busy}
                        className="sr-only peer"
                      />
                      <div className="w-9 h-5 bg-slate-200 rounded-full peer peer-checked:bg-[#1A1A1A] peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all"></div>
                    </label>
                  </div>
                );
              })}
            </div>
          </section>
        )}
      </div>
    </>
  );
}
